import { useEffect, useState } from "react";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { Grid, Switch, Typography } from "@mui/material";
import type { PermissionState } from "@capacitor/core";
import * as Sentry from "@sentry/react";
import { useChatTokenQuery } from "Api/Queries/Chat/useChatTokenQuery";
import { useChatPushDeviceToggle } from "Hooks/Chat/useChatPushDeviceToggle";
import { useStreamChatClient } from "Hooks/Chat/useStreamChatClient";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  getChatPushPermission,
  getChatPushPreference,
} from "Infrastructure/Capacitor/chatPushDevice";
import { Resources, useResource } from "Translations/Resources";
import { isAuthenticated } from "Utils/AuthUtils";
import styled from "styled-components";

const StyledGrid = styled(Grid)`
  color: ${(props) => props.theme.palette.text.secondary};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};

  &.MuiGrid-container {
    padding-left: 0;
    padding-right: 0;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  svg {
    height: 16px;
    width: 16px;
  }
`;

/**
 * "Chat notifications on this device" — the later-changes control for the chat push opt-in on the
 * native shells (the first ask is the system prompt at the chat gate). Device-local state, so it
 * reads as a property of this phone, not the account. Once the OS permission is denied only the
 * system settings can undo it, so the switch is shown off and disabled with a hint instead of a
 * dead toggle. Render only inside a native shell (`isNativePlatform()`).
 */
export const ChatPushDeviceSettingsRow = () => {
  const [permission, setPermission] = useState<PermissionState | null>(null);
  const [isEnabled, setIsEnabled] = useState(
    () => getChatPushPreference() === "on",
  );
  const { t } = useResource();
  const user = useLoggedInUser();
  const { data: chatToken } = useChatTokenQuery(
    isAuthenticated(user),
    user?.activeContextPublicID,
  );
  const { client } = useStreamChatClient(chatToken);
  const { toggle, isBusy } = useChatPushDeviceToggle(client);

  useEffect(() => {
    getChatPushPermission()
      .then(setPermission)
      .catch((error) =>
        Sentry.captureException(error, { tags: { feature: "chatPush" } }),
      );
  }, []);

  const isDenied = permission === "denied";
  const isChecked = isEnabled && permission === "granted";

  const handleChange = async (checked: boolean) => {
    const { permission, isEnabled } = await toggle(checked);
    setPermission(permission);
    setIsEnabled(isEnabled);
  };

  return (
    <StyledGrid
      container
      display="flex"
      justifyContent="center"
      alignItems="center"
      py={4}
    >
      <Grid size={{ xs: 1 }}>
        <IconWrapper>
          <NotificationsNoneOutlinedIcon />
        </IconWrapper>
      </Grid>
      <Grid size={{ xs: 9 }}>
        <Typography fontSize={16}>
          {t(Resources.Chat.PushNotifications.DeviceToggleLabel)}
        </Typography>
        <Typography variant="body2">
          {isDenied
            ? t(Resources.Chat.PushNotifications.DeniedHint)
            : t(Resources.Chat.PushNotifications.DeviceToggleDescription)}
        </Typography>
      </Grid>
      <Grid size={{ xs: 2 }} justifyContent="flex-end" display="flex">
        <Switch
          checked={isChecked}
          disabled={isDenied || isBusy || !client || permission === null}
          onChange={(_, checked) => void handleChange(checked)}
        />
      </Grid>
    </StyledGrid>
  );
};
